import React from 'react'
import Button from '@material-ui/core/Button'
import Card from '@material-ui/core/Card'
import CircularProgress from '@material-ui/core/CircularProgress'
import Collapse from '@material-ui/core/Collapse'
import Typography from '@material-ui/core/Typography'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorIcon from '@material-ui/icons/Error'
import WarningIcon from '@material-ui/icons/Warning'
import { makeStyles } from '@material-ui/core/styles'

import { postTeacherResources } from './common/utils'
import { useLogin } from './common/LoginContext'

/**
 * Teacher Content — publish the Teacher Resources Drive to the dashboard.
 *
 * The shape is a preflight (D28): "Check Drive content" runs every rule and
 * reports what it found, and Publish stays disabled until nothing is blocking.
 * The point is that a problem is seen here, before teachers see it, and each
 * finding says which file and what to do about it.
 *
 * Every judgement is the server's. This page renders `checks`, `findings` and
 * `counts` as they arrive and sends back the token it was given; it never
 * decides what is publishable, so it cannot disagree with the endpoint that
 * enforces it.
 */

const useStyles = makeStyles(theme => ({
  page: { width: '100%', maxWidth: 760, textAlign: 'left' },
  card: { padding: theme.spacing(2.5, 3), marginBottom: theme.spacing(2) },
  headRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  title: { fontWeight: 600 },
  sub: { color: theme.palette.text.secondary, marginTop: 2 },
  actions: { display: 'flex', gap: theme.spacing(1), alignItems: 'center' },

  checkRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 0),
    borderBottom: '1px solid #F0F0F0',
    '&:last-child': { borderBottom: 'none' },
  },
  checkLabel: { flexGrow: 1 },
  pill: {
    fontFamily: 'Menlo, Consolas, monospace',
    fontSize: '0.66rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    borderRadius: 99,
    padding: '4px 9px',
    whiteSpace: 'nowrap',
  },
  pillOk: { color: '#2E7D32', backgroundColor: '#E8F5E9' },
  pillError: { color: '#C4472F', backgroundColor: '#FDEEEA' },
  pillWarn: { color: '#9A6C10', backgroundColor: '#FBF3E2' },

  finding: {
    display: 'flex',
    gap: theme.spacing(1.25),
    alignItems: 'flex-start',
    border: '1px solid #EEE',
    borderLeftWidth: 4,
    borderRadius: 6,
    padding: theme.spacing(1.25, 1.5),
    marginTop: theme.spacing(1),
  },
  findingError: { borderLeftColor: '#C4472F', backgroundColor: '#FDF4F2' },
  findingWarn: { borderLeftColor: '#D9A514', backgroundColor: '#FCF8EC' },
  findingPath: {
    fontFamily: 'Menlo, Consolas, monospace',
    fontSize: '0.74rem',
    fontWeight: 600,
    wordBreak: 'break-word',
  },
  fix: { color: theme.palette.text.secondary, display: 'block', marginTop: 2 },

  counts: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1.5),
    fontVariantNumeric: 'tabular-nums',
  },
  changeTable: { width: '100%', borderCollapse: 'collapse', marginTop: 8 },
  changeCell: {
    padding: '6px 8px',
    borderBottom: '1px solid #F2F2F2',
    fontSize: '0.84rem',
  },
  tag: {
    fontFamily: 'Menlo, Consolas, monospace',
    fontSize: '0.66rem',
    fontWeight: 700,
    borderRadius: 4,
    padding: '3px 7px',
  },
  tagAdd: { color: '#2E7D32', backgroundColor: '#E8F5E9' },
  tagUpdate: { color: '#1565C0', backgroundColor: '#E8F0FB' },
  tagDelete: { color: '#C4472F', backgroundColor: '#FDEEEA' },
  banner: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 6,
    marginBottom: theme.spacing(2),
  },
  bannerOk: { backgroundColor: '#E8F5E9', color: '#1B5E20' },
  bannerBad: { backgroundColor: '#FDEEEA', color: '#8C2F1D' },
}))

/** Show at most this many change rows before collapsing to a count. */
const MAX_ROWS = 40

function formatWhen(iso) {
  if (!iso) {
    return 'never'
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return 'never'
  }
  return date.toLocaleString()
}

function CheckRow({ check }) {
  const classes = useStyles()
  const failed = !check.passed
  const isError = check.severity === 'error'
  const pillClass = !failed ?
    classes.pillOk :
    isError ? classes.pillError : classes.pillWarn
  const plural = check.findingCount === 1 ? '' : 'S'
  const label = !failed ?
    'PASS' :
    `${check.findingCount} ${isError ? 'ERROR' : 'WARNING'}${plural}`

  return (
    <div className={classes.checkRow}>
      {!failed ?
        <CheckCircleIcon fontSize='small' style={{ color: '#4CAF50' }} /> :
        isError ?
          <ErrorIcon fontSize='small' style={{ color: '#C4472F' }} /> :
          <WarningIcon fontSize='small' style={{ color: '#D9A514' }} />}
      <Typography variant='body2' className={classes.checkLabel}>
        {check.label}
      </Typography>
      <span className={`${classes.pill} ${pillClass}`}>{label}</span>
    </div>
  )
}

function Finding({ finding }) {
  const classes = useStyles()
  const isError = finding.severity === 'error'
  return (
    <div
      className={`${classes.finding} ${isError ? classes.findingError : classes.findingWarn}`}
    >
      <div>
        <span className={classes.findingPath}>{finding.path}</span>
        <Typography variant='body2'>{finding.message}</Typography>
        {finding.fix &&
          <Typography variant='caption' className={classes.fix}>
            {`Fix: ${finding.fix}`}
          </Typography>}
      </div>
    </div>
  )
}

function ChangeTable({ changes }) {
  const classes = useStyles()
  const rows = [
    ...changes.adds.map(c => ({ ...c, kind: 'ADD' })),
    ...changes.updates.map(c => ({ ...c, kind: 'UPDATE' })),
    ...changes.deletes.map(c => ({ ...c, kind: 'REMOVE' })),
  ]
  if (rows.length === 0) {
    return (
      <Typography variant='body2' color='textSecondary'>
        {'Nothing has changed since the last publish.'}
      </Typography>
    )
  }
  const shown = rows.slice(0, MAX_ROWS)
  const tagFor = kind =>
    kind === 'ADD' ?
      classes.tagAdd :
      kind === 'UPDATE' ? classes.tagUpdate : classes.tagDelete

  return (
    <div style={{ overflowX: 'auto' }}>
      <table className={classes.changeTable}>
        <tbody>
          {shown.map(row =>
            <tr key={`${row.kind}:${row.key}`}>
              <td className={classes.changeCell} style={{ width: 90 }}>
                <span className={`${classes.tag} ${tagFor(row.kind)}`}>
                  {row.kind}
                </span>
              </td>
              <td className={classes.changeCell}>{row.name || row.key}</td>
              <td className={classes.changeCell} style={{ color: '#777' }}>
                {row.folder || row.key}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {rows.length > shown.length &&
        <Typography variant='caption' color='textSecondary'>
          {`…and ${rows.length - shown.length} more`}
        </Typography>}
    </div>
  )
}

function TeacherContentPanel() {
  const classes = useStyles()
  const [loginState] = useLogin()
  const user = loginState?.user

  const [busy, setBusy] = React.useState(null) // 'check' | 'apply' | null
  const [check, setCheck] = React.useState(null)
  const [result, setResult] = React.useState(null)
  const [error, setError] = React.useState(null)
  const [showChanges, setShowChanges] = React.useState(false)

  const call = async (path, body) => {
    const resp = await postTeacherResources(path, user, body)
    if (resp?.status === 'logged out') {
      throw new Error('You are signed out. Sign in again to continue.')
    }
    if (resp.status === 403) {
      throw new Error('This page is for Firia administrators.')
    }
    const data = await resp.json().catch(() => null)
    if (!data) {
      throw new Error('The server sent a response we could not read.')
    }
    return { status: resp.status, data }
  }

  const runCheck = async () => {
    setBusy('check')
    setError(null)
    setResult(null)
    try {
      const { data } = await call('sync/check')
      setCheck(data)
      setShowChanges(false)
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(null)
    }
  }

  const runPublish = async () => {
    setBusy('apply')
    setError(null)
    try {
      // The token pins the check that was actually reviewed. A 409 means the
      // drive moved underneath it, so the honest response is to check again
      // rather than to retry the publish.
      const { status, data } = await call('sync/apply', { token: check?.token })
      if (status === 409) {
        setError(data.message || 'The Drive folder changed. Check it again.')
        setCheck(null)
        return
      }
      setResult(data)
      setCheck(null)
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(null)
    }
  }

  const blocked = check ? !check.publishable : true

  return (
    <div className={classes.page}>
      <Card className={classes.card}>
        <div className={classes.headRow}>
          <div>
            <Typography variant='subtitle1' className={classes.title}>
              {'Teacher Content'}
            </Typography>
            <Typography variant='caption' className={classes.sub}>
              {`Publishes the Teacher Resources Drive to the dashboard · last published: ${formatWhen(check?.lastSyncedAt || result?.syncedAt)}`}
            </Typography>
          </div>
          <div className={classes.actions}>
            {busy === 'check' && <CircularProgress size={20} />}
            <Button
              variant='contained'
              color='primary'
              disabled={Boolean(busy)}
              onClick={runCheck}
            >
              {check ? 'Check again' : 'Check Drive content'}
            </Button>
          </div>
        </div>
      </Card>

      {error &&
        <div className={`${classes.banner} ${classes.bannerBad}`}>
          <Typography variant='body2'>{error}</Typography>
        </div>}

      {result &&
        <Card className={classes.card}>
          <div className={`${classes.banner} ${classes.bannerOk}`}>
            <Typography variant='body2'>
              {`Published. ${result.counts.added} added, ${result.counts.updated} updated, ${result.counts.deleted} removed.`}
            </Typography>
          </div>
          {result.failures?.length > 0 &&
            <Typography variant='body2' color='error'>
              {`${result.failures.length} file(s) could not be copied and were left out. They will be retried by the next publish.`}
            </Typography>}
        </Card>}

      {check &&
        <Card className={classes.card}>
          <Typography variant='subtitle2' className={classes.title}>
            {'Check results'}
          </Typography>
          <div style={{ marginTop: 8 }}>
            {check.checks.map(c => <CheckRow key={c.id} check={c} />)}
          </div>

          {check.findings.length > 0 &&
            <div style={{ marginTop: 12 }}>
              {check.findings.map((f, i) =>
                <Finding key={`${f.rule}:${f.path}:${i}`} finding={f} />
              )}
            </div>}

          <div className={classes.counts}>
            <Typography variant='body2'>{`${check.counts.adds} new`}</Typography>
            <Typography variant='body2'>{`${check.counts.updates} changed`}</Typography>
            <Typography variant='body2'>{`${check.counts.deletes} removed`}</Typography>
            <Typography variant='body2' color='textSecondary'>
              {`${check.counts.unchanged} unchanged`}
            </Typography>
          </div>

          <div
            className={classes.headRow}
            style={{ marginTop: 16, alignItems: 'center' }}
          >
            <Button size='small' onClick={() => setShowChanges(v => !v)}>
              {showChanges ? 'Hide changes' : 'See the changes'}
            </Button>
            <div className={classes.actions}>
              {busy === 'apply' && <CircularProgress size={20} />}
              <Button
                variant='contained'
                color='primary'
                disabled={blocked || Boolean(busy)}
                onClick={runPublish}
              >
                {/*
                  A disabled button that does not say why is a dead end, so the
                  label carries the reason.
                */}
                {blocked ?
                  `Publish — fix ${check.errorCount} error${check.errorCount === 1 ? '' : 's'} first` :
                  'Publish to dashboard'}
              </Button>
            </div>
          </div>

          <Collapse in={showChanges}>
            <ChangeTable changes={check.changes} />
          </Collapse>
        </Card>}
    </div>
  )
}

export { TeacherContentPanel, formatWhen, MAX_ROWS }
